import { Quill } from 'react-quill';
import { uploadBannerImage } from 'src/services/napi';

const Delta = Quill.import('delta');
const isImageFile = (file) => file.type.startsWith('image/');
const isDataImageUrl = (source) => typeof source === 'string' && /^data:image\//i.test(source);
const needsImageUpload = (source) => source instanceof File || isDataImageUrl(source);
const getProjectId = () => JSON.parse(localStorage.getItem('currentProject'))?._id;
const convertFilesToReactQuillOperations = (files) =>
  files.map((file) => ({ insert: { image: file } }));

function convertHtmlToReactQuillOperations(quill, html) {
  const delta = quill.clipboard.convert(html);
  const containsDataImages = delta.ops.some((operation) => {
    const imageSource = operation.insert?.image;
    return isDataImageUrl(imageSource);
  });

  if (!containsDataImages) {
    return null;
  }

  return delta.ops;
}

async function toImageFile(source, index) {
  if (source instanceof File) {
    return source;
  }

  const response = await fetch(source);
  const blob = await response.blob();
  const mimeType = blob.type.split(';')[0];
  const extension = mimeType.split('/')[1].split('+')[0];
  return new File([blob], `image-${index + 1}.${extension}`, { type: mimeType });
}

function readImageInput(transfer) {
  if (!transfer) {
    return null;
  }

  const files = Array.from(transfer.files || []);
  const allFilesAreImages = files.every((file) => isImageFile(file));
  if (!allFilesAreImages) {
    return null;
  }

  const html = transfer.getData('text/html');
  const containsImageTag = /<img[\s>]/i.test(html);
  if (containsImageTag) {
    const template = document.createElement('template');
    template.innerHTML = html;
    const images = Array.from(template.content.querySelectorAll('img'));
    const containsDataImages = images.some((image) => {
      const imageSource = image.getAttribute('src');
      return isDataImageUrl(imageSource);
    });

    if (containsDataImages) {
      return { html };
    }
    if (images.length > 0) {
      return null;
    }
  }

  return files.length ? { files } : null;
}

function convertToReactQuillOperations(quill, imageInput) {
  if (imageInput.files) {
    return convertFilesToReactQuillOperations(imageInput.files);
  }

  return convertHtmlToReactQuillOperations(quill, imageInput.html);
}

function getDropRange(quill, event) {
  const doc = quill.root.ownerDocument;
  let nativeRange = null;
  if (doc.caretRangeFromPoint) {
    nativeRange = doc.caretRangeFromPoint(event.clientX, event.clientY);
  } else if (doc.caretPositionFromPoint) {
    const position = doc.caretPositionFromPoint(event.clientX, event.clientY);
    if (position) {
      nativeRange = doc.createRange();
      nativeRange.setStart(position.offsetNode, position.offset);
      nativeRange.collapse(true);
    }
  }

  if (nativeRange && quill.root.contains(nativeRange.startContainer)) {
    const selection = doc.getSelection();
    selection.removeAllRanges();
    selection.addRange(nativeRange);
  }
  const range = quill.getSelection();
  return { index: range ? range.index : quill.getLength() - 1, length: 0 };
}

async function uploadImageFile(file, projectId) {
  const formData = new FormData();
  formData.append('file', file);
  const response = await uploadBannerImage(formData, projectId);
  const url = response?.data?.file?.url;
  const isValidImageUrl = typeof url === 'string' && /^(https?:\/\/|\/)/i.test(url);

  if (!isValidImageUrl) {
    throw new Error('Image upload failed. Please try again.');
  }
  return url;
}

async function uploadImages(operations, projectId) {
  const uploadTasks = operations.map(async (operation, index) => {
    const imageSource = operation.insert?.image;

    if (!needsImageUpload(imageSource)) {
      return operation;
    }

    const file = await toImageFile(imageSource, index);
    const imageUrl = await uploadImageFile(file, projectId);

    return { ...operation, insert: { image: imageUrl } };
  });

  const uploadedOperations = await Promise.all(uploadTasks);
  return new Delta(uploadedOperations);
}

export function attachImageUploads(quill, { onError, onUploadingChange }) {
  const { root } = quill;
  let isDisposed = false;
  let isUploading = false;

  function getSelectionRange() {
    return quill.getSelection(true) || { index: quill.getLength() - 1, length: 0 };
  }

  function chooseImage() {
    if (isDisposed || isUploading || !quill.isEnabled()) {
      return;
    }

    const range = getSelectionRange();
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      if (isDisposed) {
        return;
      }

      const file = input.files?.[0];
      if (!file) {
        return;
      }

      if (!isImageFile(file)) {
        onError('Please select image file.');
        return;
      }

      insertImages(convertFilesToReactQuillOperations([file]), { index: range.index, length: 0 });
    };
    input.click();
  }

  function handlePaste(event) {
    if (event.defaultPrevented || !quill.isEnabled()) {
      return;
    }

    const imageInput = readImageInput(event.clipboardData);
    if (!imageInput) {
      return;
    }

    const range = getSelectionRange();
    const operations = convertToReactQuillOperations(quill, imageInput);
    if (!operations) {
      return;
    }

    event.preventDefault();
    insertImages(operations, range);
  }

  function handleDrop(event) {
    if (event.defaultPrevented || !quill.isEnabled()) {
      return;
    }

    const imageInput = readImageInput(event.dataTransfer);
    if (!imageInput) {
      return;
    }

    const range = getDropRange(quill, event);
    const operations = convertToReactQuillOperations(quill, imageInput);
    if (!operations) {
      return;
    }

    event.preventDefault();
    insertImages(operations, range);
  }

  async function insertImages(operations, range) {
    if (isDisposed || isUploading) {
      return;
    }

    isUploading = true;
    const { index, length } = range;

    onUploadingChange(true);

    try {
      const projectId = getProjectId();
      if (!projectId) {
        throw new Error('Select a project before uploading an image.');
      }

      const uploadedDelta = await uploadImages(operations, projectId);
      if (isDisposed) {
        return;
      }

      const currentProjectId = getProjectId();
      if (currentProjectId !== projectId) {
        throw new Error('The project changed. Please insert the image again.');
      }

      const insertionIndex = Math.min(index, quill.getLength() - 1);
      quill.history.cutoff();
      const contentChange = new Delta().retain(insertionIndex).concat(uploadedDelta).delete(length);
      quill.updateContents(contentChange, 'user');
      quill.history.cutoff();
    } catch (error) {
      if (!isDisposed) {
        onError(error?.message || 'Image upload failed. Please try again.');
      }
    } finally {
      isUploading = false;
      if (!isDisposed) {
        onUploadingChange(false);
      }
    }
  }

  function dispose() {
    isDisposed = true;
    root.removeEventListener('paste', handlePaste, true);
    root.removeEventListener('drop', handleDrop, true);
  }

  root.addEventListener('paste', handlePaste, true);
  root.addEventListener('drop', handleDrop, true);

  return { chooseImage, dispose };
}
